import React, { useMemo } from "react";
import { Banner, LoadingIndicator, HelpIndicator } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { AccountActivationCardHeader } from "apps/portal/Portal/common/AccountActivation/AccountActivationCardHeader";
import { TwoFaType as TwoFaTypeEnum } from "common/types/mainEnums";
import classNames from "classnames";
import { useIsXLScreenLogin } from "apps/login/LoginPage/hooks/useIsXLScreenLogin";
import { HELPER_TOOLTIP_2FA_MESSAGE } from "common/constants/copy";
import { type Handle2FAInputs } from "apps/portal/Portal/common/AccountActivation/VerifyTwoFA";
import { useFeatureFlag } from "@jsq/flags-react";
import { VerifyTwoFAForm, type FormValue } from "./VerifyTwoFAForm";
import * as styles from "./styles_vanilla.css";
import { type UnauthenticatedArenaObjectType } from "../types";

export type VerifyTwoFAContainerProps = {
  twoFAInputErrorMessage?: string;
  shouldClearTwoFAInput: boolean;
  showCheckbox: boolean;
  showGoBackButton?: boolean;
  isComponentLoading: boolean;
  isButtonLoading?: boolean;
  twoFaType: TwoFaTypeEnum;
  handleSubmit: (values: FormValue, resetForm: () => void) => void;
  handleInputChange: (inputs: Handle2FAInputs) => void;
  sendCode: (isResend: boolean) => void;
  twoFaSmsLastFourDigits?: string;
  showBanner?: boolean;
  bannerTitle?: string;
  bannerText?: string;
  arena?: UnauthenticatedArenaObjectType;
  showPoweredBy?: boolean;
  onRecoveryLinkClick?: () => void;
  onBackButtonClick?: () => void;
  showAuthCodeTooltip?: boolean;
};

type TwilioOutageBannerProps = {
  twoFaType: TwoFaTypeEnum;
};

function TwilioOutageBanner({ twoFaType }: TwilioOutageBannerProps) {
  const displayTwilioOutageBanner = useFeatureFlag("displayTwilioOutageBanner", false);

  if (
    twoFaType === TwoFaTypeEnum.Email ||
    twoFaType === TwoFaTypeEnum.Totp ||
    !displayTwilioOutageBanner
  ) {
    return null;
  }

  return (
    <div className="stacked-lg">
      <Banner
        variant="warning"
        title="SMS Provider Delays"
        description={
          <span>
            Our telecom provider is reporting delays in delivering SMS messages to certain
            mobile phone carriers. If your authentication code does not arrive in the next few
            minutes, please try logging in again in a few hours. If you urgently require access
            during this time, please contact our support team at{" "}
            <a href="tel:+14158412722">+1 (415) 841-2722</a> or, after US business hours, email
            us at <a href="mailto:support@junipersquare.com">support@junipersquare.com</a>.
          </span>
        }
      />
    </div>
  );
}

export const VerifyTwoFAContainer = (props: VerifyTwoFAContainerProps) => {
  const {
    twoFAInputErrorMessage = "",
    shouldClearTwoFAInput,
    showCheckbox,
    showGoBackButton = false,
    isComponentLoading,
    isButtonLoading = false,
    handleSubmit,
    handleInputChange,
    sendCode,
    twoFaSmsLastFourDigits,
    twoFaType,
    showBanner = false,
    bannerTitle = "",
    bannerText = "",
    arena,
    showPoweredBy,
    onRecoveryLinkClick,
    onBackButtonClick,
    showAuthCodeTooltip = false,
  } = props;

  const xl = useIsXLScreenLogin();

  let enterAuthCodeText = "Enter the 6 digit authentication code sent to your sign-in email.";
  if (twoFaType === TwoFaTypeEnum.Sms) {
    enterAuthCodeText = `Enter the 6 digit authentication code sent to phone number ending in ${twoFaSmsLastFourDigits}.`;
  } else if (twoFaType === TwoFaTypeEnum.Totp) {
    enterAuthCodeText = "Enter the 6 digit authentication code from your authenticator app.";
  }
  let deviceType = "email";
  if (twoFaType === TwoFaTypeEnum.Sms) {
    deviceType = "phone";
  } else if (twoFaType === TwoFaTypeEnum.Totp) {
    deviceType = "authenticator app";
  }
  const recoveryLinkText = `Can't access your ${deviceType}?`;

  const headlineText = useMemo(() => {
    const text = "Enter authentication code";
    const testId = "verifyTwoFaContainerTitle";
    const Component = xl ? "h1" : "h2";
    return (
      <Component className={classNames(Component, "align-center")} data-testid={testId}>
        {text}
      </Component>
    );
  }, [xl]);

  if (isComponentLoading) {
    return <LoadingIndicator />;
  }

  return (
    <div>
      {showPoweredBy && <AccountActivationCardHeader />}
      <div>
        <div className={styles.FormHeader}>{headlineText}</div>
        <p
          className={classNames({
            "text-lg": xl,
            "text-md": !xl,
            secondary: true,
            "stacked-xl": xl,
            "stacked-lg": !xl,
            "align-center": true,
          })}
          data-cy="enter-authentication-code-text"
        >
          {enterAuthCodeText}
          {showAuthCodeTooltip && twoFaType !== TwoFaTypeEnum.Totp && (
            <>
              {" "}
              <HelpIndicator content={HELPER_TOOLTIP_2FA_MESSAGE}>
                <Icon
                  type="question-circle"
                  variant="outline"
                  aria-label="question-icon"
                  data-testid="auth-code-tooltip"
                />
              </HelpIndicator>
            </>
          )}
          {onRecoveryLinkClick && (
            <p
              className={classNames({
                "stacked-bottom-sm": true,
                secondary: true,
                "align-center": true,
                "text-sm": true,
              })}
            >
              <button
                onClick={onRecoveryLinkClick}
                className={styles.RecoveryLink}
                type="button"
                data-cy="access-recovery-link"
              >
                {recoveryLinkText}
              </button>
            </p>
          )}
        </p>
        <TwilioOutageBanner twoFaType={twoFaType} />
        {showBanner && (
          <div className="stacked-lg">
            <Banner variant="primary" title={bannerTitle} description={bannerText} />
          </div>
        )}
        <VerifyTwoFAForm
          twoFAInputErrorMessage={twoFAInputErrorMessage}
          shouldClearTwoFAInput={shouldClearTwoFAInput}
          showCheckbox={showCheckbox}
          showGoBackButton={showGoBackButton}
          onBackButtonClick={onBackButtonClick}
          isButtonLoading={isButtonLoading}
          handleSubmit={handleSubmit}
          handleInputChange={handleInputChange}
          sendCode={sendCode}
          arena={arena}
          twoFaType={twoFaType}
        />
      </div>
    </div>
  );
};

export default VerifyTwoFAContainer;
